import { Spinner } from '@heroui/react';
import { StatusChip, type ChipTone } from '@/components/common/StatusChip';
import { useDomainOnboarding } from '@/features/domains/hooks';
import type { OnboardingState } from '@/features/domains/types';

const label: Record<OnboardingState, { text: string; tone: ChipTone }> = {
  done: { text: 'Done', tone: 'success' },
  pending: { text: 'In progress', tone: 'info' },
  'action-required': { text: 'Action needed', tone: 'warning' },
  'not-started': { text: 'Not started', tone: 'neutral' },
  unknown: { text: 'Unknown', tone: 'neutral' },
};

/**
 * Onboarding checklist for one domain: ownership, DNS, email, webmail HTTPS and website,
 * all from live state on the server. Each row says what to do next; nothing here is inferred
 * in the browser.
 */
export function OnboardingChecklist({ domainId }: { domainId: string }) {
  const { data, isLoading, isError } = useDomainOnboarding(domainId);
  if (isLoading) return <div className="mb-4 flex justify-center rounded-xl border border-border bg-surface p-5"><Spinner size="sm" label="Checking setup…" /></div>;
  if (isError || !data) return null;
  const complete = data.completed === data.total;
  return (
    <section aria-label="Setup checklist" className="mb-4 rounded-xl border border-border bg-surface p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Setup checklist</h2>
        <span className="text-xs text-fg-muted">{complete ? 'All set' : `${data.completed} of ${data.total} done`}</span>
      </div>
      <ol className="space-y-3">
        {data.steps.map((step) => (
          <li key={step.key} className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-fg">{step.title}</p>
              <p className="mt-0.5 text-xs leading-5 text-fg-muted">{step.detail}</p>
            </div>
            <StatusChip tone={label[step.state]?.tone ?? 'neutral'}>{label[step.state]?.text ?? step.state}</StatusChip>
          </li>
        ))}
      </ol>
    </section>
  );
}
